<template>
  <DesktopClient v-if="isDesktopClient" />

  <div v-else class="tai-xiu-feature">
    <CockFight v-if="activeProduct === 'cockfight'" class="cockfight-background" />

    <LayoutMainPage v-else class="full-tai-xiu">
      <main
        ref="txContent"
        class="full-tai-xiu__content"
        :class="{ 'full-tai-xiu__content--sticky-video': isVideoSticky }"
      >
        <TaiXiuBoardLayout v-if="activeTable">
          <template #top>
            <TaiXiuFullBoardTop
              :table="activeTable"
              :last-result="lastResult"
              :is-rolling="isRolling"
            />
          </template>
          <BetBoard
            class="full-tai-xiu__board"
            full
            v-bind="fullBoardProps(activeTable)"
            v-on="fullBoardListeners"
          />
        </TaiXiuBoardLayout>
      </main>
    </LayoutMainPage>

    <Transition name="bar-slide">
      <TableBar
        v-if="showTableBar"
        class="feature-table-bar"
        :tables="tables"
        :active-product="activeProduct"
        :active-table-id="activeTable ? activeTable.id : ''"
        :quick-bet-table-id="quickBetTable ? quickBetTable.id : ''"
        :quick-play-enabled="quickPlayEnabled"
        @close="onTableBarClose"
        @quick-play="setQuickPlay"
        @select-table="selectTable"
      />
    </Transition>

    <Transition name="quick-fade">
      <QuickPlaySheet
        v-if="quickBetTable"
        v-bind="boardProps(quickBetTable)"
        v-on="boardListeners"
        @close="closeQuickBet"
      />
    </Transition>

    <MenuPopup v-if="activeProduct === 'tai-xiu'" fixed />
    <!-- The cockfight page mounts its own error popup. -->
    <BetErrorPopup v-if="activeProduct === 'tai-xiu'" />
  </div>
</template>

<script>
import { isMobile } from 'mobile-device-detect';
import { mapActions, mapGetters } from 'vuex';
import CockFight from '@/pages/cock-fight.vue';
import LayoutMainPage from '@/layouts/main-page.vue';
import BetBoard from '@/components/tai-xiu/bet-board.vue';
import QuickPlaySheet from '@/components/tai-xiu/quick-play-sheet.vue';
import TableBar from '@/components/tai-xiu/table-bar.vue';
import TaiXiuBoardLayout from '@/components/tai-xiu/tai-xiu-board-layout.vue';
import TaiXiuFullBoardTop from '@/components/tai-xiu/tai-xiu-full-board-top.vue';
import MenuPopup from '@/components/menus/menu-popup.vue';
import BetErrorPopup from '@/components/popup/bet-error-popup.vue';
import DesktopClient from '@/components/desktop/desktop-client.vue';
import { disconnectTaiXiuConnection, initTaiXiuConnection } from '@/utils/websocket';
import { TAI_XIU_FOCUS } from '@/utils/constant';

export default {
  name: 'TaiXiuFeature',
  components: {
    CockFight,
    LayoutMainPage,
    BetBoard,
    QuickPlaySheet,
    TableBar,
    TaiXiuBoardLayout,
    TaiXiuFullBoardTop,
    MenuPopup,
    BetErrorPopup,
    DesktopClient,
  },
  data() {
    return {
      // Float video: the board's video pins to the top once it scrolls away,
      // the same behaviour the cockfight page has.
      isVideoSticky: false,
      videoOffsetTop: 0,
      isMobileDevice: isMobile,
    };
  },
  computed: {
    ...mapGetters('general', ['token', 'oddsType', 'config']),
    isFloatingVideo() {
      return this.config.floatVideo;
    },
    ...mapGetters('taiXiu', [
      'tables',
      'sessionSlots',
      'activeProduct',
      'activeTable',
      'quickBetTable',
      'isTableBarOpen',
      'quickPlayEnabled',
      'activeChip',
      'draftBets',
      'placedBets',
      'canUndo',
      'feedback',
      'isSubmitting',
      'isRolling',
      'lastResult',
    ]),
    taiXiuArenaId() {
      return (this.activeTable && this.activeTable.arenaId) || '';
    },
    isDesktopClient() {
      // The route is already gated on isClientV2; the only extra question here
      // is whether this is a desktop viewport.
      return !this.isMobileDevice;
    },
    // On mobile only one product is on screen, so the socket follows
    // `activeProduct`. The desktop client opens one feed per arena it shows
    // (`syncPaneFightConnections`), so this single-table feed stays off there.
    taiXiuSocketWanted() {
      if (this.isDesktopClient) return false;
      return this.activeProduct === 'tai-xiu';
    },
    taiXiuSocketKey() {
      if (!this.taiXiuSocketWanted) return '';
      return [this.token, this.oddsType, this.taiXiuArenaId].join(':');
    },
    showTableBar() {
      if (this.activeProduct === 'tai-xiu') {
        return !!(this.activeTable && this.tables.length);
      }
      return this.isTableBarOpen || !!this.quickBetTable;
    },
    boardListeners() {
      return {
        bet: this.addBet,
        'select-chip': this.selectChip,
        undo: this.undoBet,
        clear: this.clearBets,
        submit: this.submitBets,
      };
    },
    fullBoardListeners() {
      return {
        ...this.boardListeners,
        'select-table': this.selectTable,
        'quick-play': this.setQuickPlay,
      };
    },
  },
  watch: {
    // The toggle can be flipped while the board is open.
    isFloatingVideo(enabled) {
      if (enabled) this.$nextTick(this.bindVideoFloat);
      else this.unbindVideoFloat();
    },
    activeProduct(product, previous) {
      // Runs before the re-render, so the outgoing product's scroller is still
      // mounted and its position can be kept.
      this.saveProductScroll(previous);
      // The board is torn down and rebuilt when the product changes.
      this.unbindVideoFloat();
      this.$nextTick(() => {
        this.bindVideoFloat();
        this.restoreProductScroll(product);
      });
    },
    taiXiuSocketKey: {
      immediate: true,
      handler() {
        this.syncTaiXiuSocket();
      },
    },
  },
  mounted() {
    this.resetBalance();
    this.getWallet();
    this.getCockfightArenas();
    this.getTaiXiuArenas().then(this.openFocusedTable);
    this.getJackpots();
    this.updateAnnouncement();
    window.addEventListener('keydown', this.handleKeydown);
    this.$nextTick(this.bindVideoFloat);
  },
  created() {
    // Per-product scroll position, keyed by `activeProduct`. Not reactive.
    this.productScroll = {};
  },
  beforeDestroy() {
    this.cancelScrollRestore();
    disconnectTaiXiuConnection();
    window.removeEventListener('keydown', this.handleKeydown);
    this.unbindVideoFloat();
  },
  methods: {
    /*
     * Float video, as on the cockfight page: the board's video pins to the top
     * of the board once it has scrolled past. Tài Xỉu never had it wired up at
     * all, so the setting did nothing here.
     */
    bindVideoFloat() {
      const content = this.$refs.txContent;
      if (!content || !this.isMobileDevice || !this.isFloatingVideo) return;
      this.captureVideoOffset();
      content.addEventListener('scroll', this.handleVideoScroll, { passive: true });
    },
    unbindVideoFloat() {
      const content = this.$refs.txContent;
      if (content) content.removeEventListener('scroll', this.handleVideoScroll);
      this.isVideoSticky = false;
    },
    captureVideoOffset() {
      const content = this.$refs.txContent;
      const video = content && content.querySelector('.tai-xiu-full-video');
      if (video) this.videoOffsetTop = video.offsetTop;
    },
    handleVideoScroll() {
      const content = this.$refs.txContent;
      if (!content || !this.isFloatingVideo) return;
      if (!this.isVideoSticky) this.captureVideoOffset();
      this.isVideoSticky = content.scrollTop > this.videoOffsetTop;
    },

    /*
     * Mobile swaps cockfight and Tài Xỉu with v-if, so each switch rebuilt the
     * other product's scroller at the top. Keep each product's own position.
     */
    productScroller(product) {
      // Embedded here the cockfight container has no fixed height, so it is the
      // document that scrolls, not `.container`.
      if (product === 'cockfight') return document.scrollingElement || document.documentElement;
      return this.$refs.txContent;
    },
    saveProductScroll(product) {
      if (this.isDesktopClient || !product) return;
      this.cancelScrollRestore();
      const scroller = this.productScroller(product);
      if (scroller) this.productScroll[product] = scroller.scrollTop;
    },
    restoreProductScroll(product) {
      if (this.isDesktopClient) return;
      const target = this.productScroll[product] || 0;
      if (!target) return;
      // The video and charts can arrive after mount, so the scroller may still
      // be too short; keep trying for a moment unless the member scrolls first.
      const deadline = Date.now() + 1500;
      const step = () => {
        this.scrollRestoreFrame = null;
        const scroller = this.productScroller(product);
        if (!scroller || this.activeProduct !== product) return;
        scroller.scrollTop = target;
        if (Math.abs(scroller.scrollTop - target) > 1 && Date.now() < deadline) {
          this.scrollRestoreFrame = requestAnimationFrame(step);
        } else {
          this.cancelScrollRestore();
        }
      };
      window.addEventListener('touchstart', this.cancelScrollRestore, { passive: true });
      window.addEventListener('wheel', this.cancelScrollRestore, { passive: true });
      step();
    },
    cancelScrollRestore() {
      if (this.scrollRestoreFrame) cancelAnimationFrame(this.scrollRestoreFrame);
      this.scrollRestoreFrame = null;
      window.removeEventListener('touchstart', this.cancelScrollRestore);
      window.removeEventListener('wheel', this.cancelScrollRestore);
    },

    ...mapActions('general', ['getWallet', 'resetBalance']),
    ...mapActions('general', { getCockfightArenas: 'getArenas' }),
    ...mapActions('jackpot', ['getJackpots']),
    ...mapActions('announcement', ['updateAnnouncement']),
    ...mapActions('taiXiu', [
      'toggleTableBar',
      'closeTableBar',
      'setQuickPlay',
      'selectTable',
      'selectCockfight',
      'closeQuickBet',
      'selectChip',
      'addBet',
      'undoBet',
      'clearBets',
      'submitBets',
    ]),
    ...mapActions('taiXiu', { getTaiXiuArenas: 'getArenas' }),
    /*
     * `focus=tx` launch: mobile opens on the first open Tài Xỉu table rather
     * than on cockfight. The desktop client's pane A already defaults to Tài
     * Xỉu. Skipped if the member has already left cockfight.
     */
    openFocusedTable() {
      if (this.isDesktopClient || this.$store.state.general.general.focus !== TAI_XIU_FOCUS) return;
      if (this.activeProduct !== 'cockfight' || this.quickBetTable) return;
      const table = this.tables.find((item) => item.isAvailable !== false);
      if (table) this.selectTable(table.id);
    },
    syncTaiXiuSocket() {
      if (!this.taiXiuSocketWanted || !this.token || !this.taiXiuArenaId) {
        disconnectTaiXiuConnection();
        return;
      }
      initTaiXiuConnection(this.token, this.oddsType, this.taiXiuArenaId);
    },
    boardProps(table) {
      return {
        table,
        activeChip: this.activeChip,
        draftBets: this.draftBets,
        placedBets: this.placedBets,
        canUndo: this.canUndo,
        feedback: this.feedback,
        isSubmitting: this.isSubmitting,
        isRolling: this.isRolling,
        lastResult: this.lastResult,
      };
    },
    fullBoardProps(table) {
      return this.boardProps(table);
    },
    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.quickBetTable) this.closeQuickBet();
      else if (this.isTableBarOpen) this.closeTableBar();
    },
    onTableBarClose() {
      if (this.quickBetTable) {
        this.closeQuickBet();
        return;
      }
      if (this.activeProduct === 'tai-xiu') {
        this.selectCockfight();
        return;
      }
      this.closeTableBar();
    },
  },
};
</script>

<style lang="scss" scoped>
.tai-xiu-feature {
  position: relative;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  background: #061820;
}

.cockfight-background {
  transition: filter 0.2s ease;
}

// v2.1: footer panel 66 + table bar 34 = the 100px footer.
$footer-height: 66px;
$table-bar-height: 34px;

/*
 * Mobile cockfight: the page pads its scroller for its own footer only (30px).
 * Inside this feature the table bar and the mobile footer are fixed over its
 * bottom, so the roads sat under them -- and the content fitted the scroller
 * exactly, so there was nothing to scroll them out with. Pad by both bars.
 * Scoped, so it outweighs `.cock-fight-body .container.vertical`.
 */
.cockfight-background ::v-deep .container.vertical {
  padding-bottom: calc(#{$footer-height} + #{$table-bar-height} + env(safe-area-inset-bottom, 0px));
}

.full-tai-xiu {
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: #061820;
  display: flex;
  flex-direction: column;
  padding-bottom: calc(
    #{$footer-height} + #{$table-bar-height} + env(safe-area-inset-bottom, 0px)
  );
  box-sizing: border-box;

  ::v-deep .header-wrapper {
    flex: 0 0 auto;
  }
}

.full-tai-xiu__content {
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  margin: 0 auto;
  padding: 0;
  box-sizing: border-box;
}

/*
 * Float video. The player pins to the top of the board once it has scrolled
 * past, and the board keeps its place by reserving the height the player left
 * behind -- 16:9, the ratio the player itself uses.
 */
.full-tai-xiu__content--sticky-video {
  padding-top: 56.25%;

  ::v-deep .tai-xiu-full-video {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 9990;
    border-radius: 0;
  }
}

.product-heading {
  display: none;
}

/*
 * v2.1 mobile Tài Xỉu board (TxBoard "md" in 235): ALL PAY + main row, TRIPLE +
 * triples, the two sum rows and SINGLE/DOUBLE/TRIPLE + dice, 3px apart --
 * 15 + 63 | 3 + 15 + 25 | 3 + 2 x 33 | 3 + 15 + 25. Scoped to this page, so
 * the desktop panes keep their own sizes.
 */
.full-tai-xiu ::v-deep .tai-xiu-board.full {
  .full-board-body {
    flex: 0 0 auto;
    gap: 0;

    > * {
      flex-shrink: 0;
    }
  }

  .tx-info-bar {
    height: 15px;
    min-height: 15px;
    margin-top: 0;
    padding: 0;
    border-radius: 0;
    background: $ga-navy-800;
    color: $ga-gold-300;
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 0.06em;
  }

  .tx-triple-section,
  .full-sum-rows,
  .tx-info-bar--single {
    margin-top: 3px;
  }

  .tx-main-row {
    height: 63px;
    min-height: 63px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    box-sizing: border-box;
  }

  .tx-main-bet > span:not(.sub) {
    font-size: 26px;
    font-weight: 800;
    line-height: 1;
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
  }

  .tx-main-bet .sub,
  .tx-odd-even__btn small {
    margin-top: 1px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.03em;
  }

  .tx-odd-even__btn span {
    font-size: 15px;
    font-weight: 800;
  }

  .tx-triple-row {
    height: 25px;
    min-height: 25px;
    border-color: $ga-tx-cell-border;
  }

  .full-sum-rows {
    border-color: $ga-tx-cell-border;
  }

  .sum-grid button {
    height: 33px;
    min-height: 33px;
    padding: 0 1px;
  }

  .sum-grid strong {
    font-size: 15px;
    line-height: 1;
  }

  .sum-grid small {
    font-size: 9px;
    color: #c9d7ee;
  }

  .bet-dice-market {
    margin-bottom: 0;
  }

  .bet-dice-market__face {
    height: 25px;
    min-height: 25px;
    border-color: $ga-tx-cell-divider;
    border-radius: 0;

    img {
      width: 18px;
      height: 18px;
    }
  }

  // The chip row: 52 tall, pinned while the board scrolls, 38px chips.
  .bet-board-actions--fancy {
    height: 52px;
    margin-top: 8px;
    background: #061820;
  }

  .bet-chip-selector__option {
    flex-basis: 38px;
    width: 38px;
    height: 38px;
    min-height: 38px;
  }

  // Undo: a 32px button over a plain 8.5px label (v2.1 ChipRow), not the badge.
  .bet-board-actions__undo {
    width: 32px;
    height: auto;
    gap: 2px;

    img {
      width: 32px;
      height: 32px;
    }

    small {
      width: auto;
      height: auto;
      margin-top: 0;
      background: transparent;
      color: #8fb3e0;
      font-size: 8.5px;
      font-weight: 600;
      line-height: 1;
      letter-spacing: 0.04em;
    }
  }

  .bet-history-charts {
    height: 120px;
    margin-top: 8px;
  }
}

.feature-table-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: calc(#{$footer-height} + env(safe-area-inset-bottom, 0px));
  z-index: 111;
  width: 100%;
  margin: 0 auto;
  padding: 4px 10px 0;
  box-sizing: border-box;
}

.bar-slide-enter-active,
.bar-slide-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.bar-slide-enter,
.bar-slide-leave-to {
  transform: translateY(18px);
  opacity: 0;
}

.quick-fade-enter-active,
.quick-fade-leave-active {
  transition: opacity 0.18s ease;
}

.quick-fade-enter,
.quick-fade-leave-to {
  opacity: 0;
}

@media screen and (orientation: landscape) and (max-height: 600px) {
  .full-tai-xiu {
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }

  .full-tai-xiu__content {
    width: min(72%, 590px);
  }

  .feature-table-bar {
    width: min(72%, 590px);
    left: auto;
    right: 8px;
    bottom: 8px;
  }
}

@media screen and (min-width: 768px) {
  // The mobile footer panel is not rendered on desktop, so no space is reserved.
  .full-tai-xiu {
    padding-bottom: 0;
  }

  .feature-table-bar {
    bottom: 14px;
  }
}
</style>
